<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
defineProps({
  proposalForm: {
    type: Object,
    required: true,
  },
  selectedCatalogSummary: {
    type: Object,
    default: null,
  },
  proposalDeadlinePreview: {
    type: String,
    default: '',
  },
  purchasePaymentInfo: {
    type: Object,
    default: () => ({ total: 0 }),
  },
  rentalAdminFeePercentage: {
    type: Number,
    default: 0,
  },
  rentalAdminFeeValue: {
    type: Number,
    default: 0,
  },
  rentalOwnerReceiveValue: {
    type: Number,
    default: 0,
  },
  rentalResidentsSituation: {
    type: String,
    default: '',
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
  formatFormCurrency: {
    type: Function,
    required: true,
  },
  contextLabel: {
    type: Function,
    required: true,
  },
  paymentLabel: {
    type: Function,
    required: true,
  },
  paymentNeedsFgts: {
    type: Function,
    required: true,
  },
  isFinancingPayment: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['openSelectedCatalogLink']);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="grid gap-3">
    <div class="rounded-lais border border-lais-border bg-lais-bg-soft p-4">
      <p
        class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Imóvel
      </p>
      <p class="mt-1 font-semibold text-lais-text">
        {{ selectedCatalogSummary?.title || 'Imóvel não selecionado' }}
      </p>
      <div
        v-if="selectedCatalogSummary?.code || selectedCatalogSummary?.link"
        class="mt-2 flex flex-wrap items-center gap-2"
      >
        <span
          v-if="selectedCatalogSummary?.code"
          class="inline-flex rounded-full bg-lais-primary-50 px-2 py-0.5 text-[11px] font-semibold text-lais-primary"
        >
          Código {{ selectedCatalogSummary.code }}
        </span>
        <button
          v-if="selectedCatalogSummary?.link"
          type="button"
          class="inline-flex items-center gap-1 rounded-full border border-lais-border bg-white px-2 py-0.5 text-[11px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
          @click="emit('openSelectedCatalogLink')"
        >
          <span class="i-lucide-globe-2 size-3.5" aria-hidden="true" />
          Abrir link
        </button>
      </div>
      <p class="text-[13px] text-lais-text-body">
        {{ selectedCatalogSummary?.location }}
      </p>
    </div>
    <div class="grid gap-3 md:grid-cols-2">
      <div class="rounded-lais border border-lais-border bg-white p-4">
        <p
          class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Interessado
        </p>
        <p class="mt-1 font-semibold text-lais-text">
          {{ proposalForm.requester_name || 'Nome não informado' }}
        </p>
        <p class="text-[13px] text-lais-text-body">
          {{
            proposalForm.requester_email ||
            proposalForm.requester_phone ||
            'Contato não informado'
          }}
        </p>
      </div>
      <div class="rounded-lais border border-lais-border bg-white p-4">
        <p
          class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Proposta
        </p>
        <p class="mt-1 font-semibold text-lais-text">
          {{ formatCurrency(proposalForm.offer_amount) }}
        </p>
        <p class="text-[13px] text-lais-text-body">
          {{ contextLabel(proposalForm.business_context) }}
          <template v-if="proposalForm.business_context === 'purchase'">
            · {{ paymentLabel(proposalForm.payment_kind) }}
          </template>
          <template v-else> · {{ rentalResidentsSituation }} </template>
        </p>
        <p
          v-if="proposalDeadlinePreview"
          class="mt-1 text-[13px] font-semibold text-lais-primary"
        >
          {{ proposalDeadlinePreview }}
        </p>
      </div>
    </div>
    <div
      v-if="proposalForm.business_context === 'purchase'"
      class="rounded-lais border border-lais-border bg-white p-4"
    >
      <p
        class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Valores da transação
      </p>
      <div class="mt-3 grid gap-2 text-[13px] sm:grid-cols-2">
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Sinal</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.earnest_payment_amount) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Entrada</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.entry_amount) }}
          </span>
        </div>
        <div
          v-if="paymentNeedsFgts(proposalForm.payment_kind)"
          class="flex justify-between gap-3"
        >
          <span class="text-lais-text-muted">Valor de FGTS</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.fgts_amount) }}
          </span>
        </div>
        <div
          v-if="isFinancingPayment(proposalForm.payment_kind)"
          class="flex justify-between gap-3"
        >
          <span class="text-lais-text-muted">Valor financiado</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.financed_amount) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Valor total da corretagem</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.brokerage_fee_value) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">ITBI</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.itbi_amount) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Cartório e registro</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.notary_fee_value) }}
          </span>
        </div>
        <div class="flex justify-between gap-3 sm:col-span-2">
          <span class="font-semibold text-lais-text">
            Custo total estimado
          </span>
          <span class="font-semibold text-lais-primary">
            {{ formatFormCurrency(purchasePaymentInfo.total) }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="proposalForm.business_context === 'rental'"
      class="rounded-lais border border-lais-border bg-white p-4"
    >
      <p
        class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Valores
      </p>
      <div class="mt-3 grid gap-2 text-[13px] sm:grid-cols-2">
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Aluguel</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.offer_amount) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">IPTU</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.rent_iptu_amount) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Condomínio</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.rent_condominium_amount) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">
            Taxa de adm. ({{ rentalAdminFeePercentage }}%)
          </span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(rentalAdminFeeValue) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Taxa mínima de adm.</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(proposalForm.minimum_admin_fee) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Proprietário vai receber</span>
          <span class="font-semibold text-lais-text">
            {{ formatFormCurrency(rentalOwnerReceiveValue) }}
          </span>
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Moradores</span>
          <span class="font-semibold text-lais-text">
            {{ rentalResidentsSituation }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="
        proposalForm.business_context === 'rental' &&
        proposalForm.custom_solicitation_type
      "
      class="rounded-lais border border-lais-border bg-white p-4"
    >
      <div class="flex items-center justify-between gap-3">
        <p
          class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Ajustes no imóvel
        </p>
        <span class="text-[12px] font-semibold text-lais-text-muted">
          1 item
        </span>
      </div>
      <p class="mt-2 text-[12px] font-semibold uppercase text-lais-primary">
        {{ proposalForm.custom_solicitation_type }}
      </p>
      <p class="mt-1 text-[13px] text-lais-text-body">
        {{ proposalForm.custom_solicitation_description }}
      </p>
    </div>
    <div
      v-if="proposalForm.notes"
      class="rounded-lais border border-lais-border bg-white p-4"
    >
      <p
        class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Observações
      </p>
      <p class="mt-1 text-[13px] text-lais-text-body">
        {{ proposalForm.notes }}
      </p>
    </div>
  </section>
</template>
